import React, { useState, useEffect, useRef } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { consultationService, patientService } from '../../services/api';
import { caisseApi } from '../../services/caisseApi';
import { Consultation, Patient, TreatmentType, Doctor, PaginatedResponse, OrdonnanceMedicament, OdontogrammeState } from '../../types';
import { X, Plus, Trash2, Smile, Pill, Banknote, CheckCircle } from 'lucide-react';

import DiagrammeDentaire, { createEmptyOdontogramme, deriveAffectedTeeth } from './DiagrammeDentaire';
import MedicamentSelector from './MedicamentSelector';
import PatientSearchSelect from '../common/PatientSearchSelect';

interface ConsultationFormProps {
  consultation?: Consultation;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ConsultationFormData {
  patient: number;
  doctor: number;
  date_consultation: string;
  motif_consultation: string;
  examen_clinique?: string;
  diagnostic?: string;
  plan_traitement?: string;
  notes?: string;
  dents_concernees?: string; // Nouveau champ pour les dents
}

const ConsultationForm: React.FC<ConsultationFormProps> = ({ consultation, onClose, onSuccess }) => {
  const [actes, setActes] = useState<any[]>([]);
  const [ordonnances, setOrdonnances] = useState<string[]>(['']);
  const [medicamentsOrdonnance, setMedicamentsOrdonnance] = useState<Omit<OrdonnanceMedicament, 'id' | 'ordonnance'>[]>([]);
  const [ordonnanceContenuLibre, setOrdonnanceContenuLibre] = useState<string>('');
  const [ordonnanceNotes, setOrdonnanceNotes] = useState<string>('');
  const [useStructuredOrdonnance, setUseStructuredOrdonnance] = useState<boolean>(true);
  const [odontogramme, setOdontogramme] = useState<OdontogrammeState>(createEmptyOdontogramme());
  const [showDentalDiagram, setShowDentalDiagram] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const location = useLocation();

  // Récupérer les données de navigation
  const navigationState = location.state as {
    patientId?: number;
    doctorId?: number;
    rendezVousId?: number;
    motif?: string;
    patientInfo?: any;
  } | null;

  // Fonction pour convertir une date GMT en format local pour datetime-local input
  const convertGMTToLocalDateTimeString = (gmtDateString: string): string => {
    const date = new Date(gmtDateString);
    // Extraire les composants de date/heure locaux
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<ConsultationFormData>({
    defaultValues: consultation ? {
      patient: consultation.patient,
      doctor: consultation.doctor,
      date_consultation: convertGMTToLocalDateTimeString(consultation.date_consultation),
      motif_consultation: consultation.motif_consultation,
      examen_clinique: consultation.examen_clinique || '',
      diagnostic: consultation.diagnostic || '',
      plan_traitement: consultation.plan_traitement || '',
      notes: consultation.notes || '',
      dents_concernees: consultation.dents_concernees || ''
    } : {
      patient: navigationState?.patientId || undefined,
      doctor: navigationState?.doctorId || undefined,
      motif_consultation: navigationState?.motif || '',
      date_consultation: convertGMTToLocalDateTimeString(new Date().toISOString())
    }
  });

  // Effet pour définir les valeurs pré-remplies depuis la navigation
  useEffect(() => {
    if (navigationState && !consultation) {
      if (navigationState.patientId) {
        setValue('patient', navigationState.patientId);
      }
      if (navigationState.doctorId) {
        setValue('doctor', navigationState.doctorId);
      }
      if (navigationState.motif) {
        setValue('motif_consultation', navigationState.motif);
      }
    }
  }, [navigationState, setValue, consultation]);

  // Effet pour charger les données existantes d'une consultation
  useEffect(() => {
    if (consultation) {
      // Charger les traitements existants
      if (consultation.treatments && consultation.treatments.length > 0) {
        setActes(consultation.treatments.map(treatment => ({
          treatment_type: treatment.treatment_type,
          quantite: treatment.quantite,
          prix_unitaire: treatment.prix_unitaire,
          notes: treatment.notes || ''
        })));
      }

      // Charger les ordonnances existantes
      if (consultation.ordonnances && consultation.ordonnances.length > 0) {
        // Ordonnances texte libre (ancien format)
        const ordonnancesLibres = consultation.ordonnances
          .filter(ord => !ord.medicaments || ord.medicaments.length === 0)
          .map(ord => ord.contenu ?? '')
          .filter((c): c is string => c !== undefined);
        
        if (ordonnancesLibres.length > 0) {
          setOrdonnances(ordonnancesLibres);
          setUseStructuredOrdonnance(false);
        }

        // Ordonnances avec médicaments (nouveau format)
        const ordonnancesAvecMedicaments = consultation.ordonnances
          .filter(ord => ord.medicaments && ord.medicaments.length > 0);
        
        if (ordonnancesAvecMedicaments.length > 0) {
          const premiereOrdonnance = ordonnancesAvecMedicaments[0];
          
          // Charger le contenu libre et les notes
          if (premiereOrdonnance.contenu) {
            setOrdonnanceContenuLibre(premiereOrdonnance.contenu);
          }
          if (premiereOrdonnance.notes) {
            setOrdonnanceNotes(premiereOrdonnance.notes);
          }
          
          // Charger les médicaments
          const medicamentsChargés = premiereOrdonnance.medicaments.map(ordMed => ({
            medicament: ordMed.medicament,
            medicament_details: ordMed.medicament_details,
            dosage: ordMed.dosage,
            frequence: ordMed.frequence,
            duree: ordMed.duree,
            instructions: ordMed.instructions || '',
            quantite: ordMed.quantite || ''
          }));
          
          setMedicamentsOrdonnance(medicamentsChargés);
          setUseStructuredOrdonnance(true);
        }
      }

      // Charger l'odontogramme (état riche). Rétro-compat : les anciennes
      // consultations n'ont que `dents_concernees` (texte) — l'odontogramme démarre vide.
      if (consultation.odontogramme && consultation.odontogramme.teeth) {
        setOdontogramme(consultation.odontogramme);
      }
    }
  }, [consultation]);

  const selectedPatientId = watch('patient');

  // Récupération du patient sélectionné pour l'âge (chargé à la demande)
  const { data: selectedPatient } = useQuery<Patient>({
    queryKey: ['patient', selectedPatientId],
    queryFn: () => patientService.getById(selectedPatientId!).then(res => res.data),
    enabled: !!selectedPatientId
  });

  // Pour une nouvelle consultation, présélectionner la dentition selon l'âge
  // (sans écraser un odontogramme déjà annoté).
  useEffect(() => {
    if (consultation) return;
    if (selectedPatient?.age === undefined) return;
    setOdontogramme(prev => {
      const vierge = Object.keys(prev.teeth).length === 0 && prev.bridges.length === 0;
      const cible = selectedPatient.age! < 12 ? 'lait' : 'adulte';
      if (!vierge || prev.dentition === cible) return prev;
      return { ...prev, dentition: cible };
    });
  }, [selectedPatient?.age, consultation]);

  // Récupération des types de traitement (tous, sans pagination)
  const { data: treatmentTypes = [] } = useQuery<TreatmentType[]>({
    queryKey: ['treatment-types', 'all'],
    queryFn: () => consultationService.getTreatmentTypes().then(res => res.data),
    staleTime: 5 * 60 * 1000, // Cache pour 5 minutes
  });

  // Debug: Afficher le nombre de types de traitement chargés
  useEffect(() => {
    if (treatmentTypes.length > 0) {
      console.log(`Types de traitement chargés: ${treatmentTypes.length} total`);
    }
  }, [treatmentTypes.length]);

  // Récupération des médecins
  const { data: doctors = [] } = useQuery<Doctor[]>({
    queryKey: ['doctors', 'all'],
    queryFn: () => consultationService.getDoctors().then(res => res.data),
    staleTime: 5 * 60 * 1000, // Cache pour 5 minutes
  });

  // Mutation pour créer/modifier une consultation
  const consultationMutation = useMutation<Consultation, Error, any>({
    mutationFn: (data) => consultation
      ? consultationService.update(consultation.id, data).then(res => res.data)
      : consultationService.create(data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] });
      onSuccess?.();
    }
  });

  // Indique si l'enregistrement doit aussi générer un bon de consultation pour la caisse
  const envoyerCaisseRef = useRef(false);
  const [caisseFeedback, setCaisseFeedback] = useState<string | null>(null);

  const onSubmit: SubmitHandler<ConsultationFormData> = async (data) => {
    try {
      // Convertir la date/heure locale en format ISO (GMT)
      const localDate = new Date(data.date_consultation);
      const isoDateString = localDate.toISOString();

      // Filtrer les traitements valides (avec type sélectionné) et nettoyer les champs
      const validTreatments = actes
        .filter(acte => acte.treatment_type && acte.treatment_type !== '' && acte.quantite > 0)
        .map(acte => ({
          treatment_type: acte.treatment_type,
          quantite: acte.quantite,
          prix_unitaire: acte.prix_unitaire,
          notes: acte.notes || ''
        }));

      // Dents affectées dérivées de l'odontogramme (rétro-compat avec le champ texte).
      // Si l'odontogramme n'a pas été utilisé, on conserve l'éventuelle valeur héritée.
      const affectedTeeth = deriveAffectedTeeth(odontogramme);
      const dentsConcernees = affectedTeeth.length
        ? affectedTeeth.join(',')
        : (consultation?.dents_concernees || '');

      const consultationData = {
        ...data,
        date_consultation: isoDateString, // Utiliser la date GMT convertie
        dents_concernees: dentsConcernees, // Liste plate (rétro-compat affichage)
        odontogramme, // État complet de l'odontogramme
        treatments: validTreatments, // Envoyer seulement les traitements valides
        ordonnances: ordonnances.filter(o => o.trim() !== '').map(contenu => ({ contenu }))
      };
      
      const savedConsultation = await consultationMutation.mutateAsync(consultationData);

      // Si nous avons des médicaments structurés ou du contenu libre/notes, créer une ordonnance structurée
      if (useStructuredOrdonnance && (medicamentsOrdonnance.length > 0 || ordonnanceContenuLibre.trim() || ordonnanceNotes.trim())) {
        const ordonnanceData = {
          contenu: ordonnanceContenuLibre.trim() || undefined,
          notes: ordonnanceNotes.trim() || undefined,
          medicaments: medicamentsOrdonnance
            .filter(med => med.dosage && med.frequence && med.duree)
            .map(med => ({
              medicament_id: med.medicament,
              dosage: med.dosage,
              frequence: med.frequence,
              duree: med.duree,
              instructions: med.instructions || '',
              quantite: med.quantite || ''
            }))
        };

        if (ordonnanceData.medicaments.length > 0 || ordonnanceData.contenu || ordonnanceData.notes) {
          await consultationService.ajouterOrdonnance(savedConsultation.id, ordonnanceData);
        }
      }

      // Uniquement des suites de soins : rien à encaisser, on clôture sans facture
      if (envoyerCaisseRef.current && suiteSeule) {
        envoyerCaisseRef.current = false;
        try {
          await consultationService.cloturerSansFacturation(savedConsultation.id);
          queryClient.invalidateQueries({ queryKey: ['consultations'] });
          setCaisseFeedback('Consultation clôturée sans facturation (suite de soins).');
          setTimeout(() => onSuccess?.(), 1200);
        } catch (e) {
          console.error('Erreur lors de la clôture sans facturation:', e);
          setCaisseFeedback("La consultation est enregistrée mais n'a pas pu être clôturée.");
        }
        return;
      }

      // Validation de la fiche -> génération du bon de consultation envoyé à la caisse
      if (envoyerCaisseRef.current) {
        envoyerCaisseRef.current = false;
        try {
          const bon = await caisseApi.envoyerConsultationCaisse(savedConsultation.id);
          queryClient.invalidateQueries({ queryKey: ['caisse'] });
          setCaisseFeedback(`Bon ${bon.numero} envoyé à la caisse (${bon.montant_total} GNF).`);
          // Laisser le temps d'afficher le message avant la fermeture
          setTimeout(() => onSuccess?.(), 1200);
          return;
        } catch (e) {
          console.error('Erreur lors de l\'envoi à la caisse:', e);
          setCaisseFeedback("La consultation est enregistrée mais l'envoi à la caisse a échoué.");
          return;
        }
      }

      onSuccess?.();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const ajouterTraitement = (): void => {
    setActes([...actes, { treatment_type: '', quantite: 1, prix_unitaire: 0 }]);
  };

  const supprimerActe = (index: number): void => {
    setActes(actes.filter((_, i) => i !== index));
  };

  const modifierTraitement = (index: number, field: string, value: any): void => {
    const nouveauxActes = [...actes];
    nouveauxActes[index] = { ...nouveauxActes[index], [field]: value };
    
    if (field === 'treatment_type') {
      const treatmentType = treatmentTypes.find(t => t.id === parseInt(value));
      if (treatmentType) {
        nouveauxActes[index].prix_unitaire = treatmentType.non_facturable ? 0 : treatmentType.base_price;
      }
    }
    
    setActes(nouveauxActes);
  };

  const ajouterOrdonnance = (): void => {
    setOrdonnances([...ordonnances, '']);
  };

  const supprimerOrdonnance = (index: number): void => {
    setOrdonnances(ordonnances.filter((_, i) => i !== index));
  };

  const modifierOrdonnance = (index: number, value: string): void => {
    const nouvellesOrdonnances = [...ordonnances];
    nouvellesOrdonnances[index] = value;
    setOrdonnances(nouvellesOrdonnances);
  };

  const isLocked = consultation?.is_locked || false;
  const estNonFacturable = (acte: { treatment_type: any }) =>
    !!treatmentTypes.find(t => t.id === Number(acte.treatment_type))?.non_facturable;
  // Que des suites de soins : la fiche se clôture sans facture au lieu de partir en caisse
  const actesRenseignes = actes.filter(a => a.treatment_type);
  const suiteSeule = actesRenseignes.length > 0 && actesRenseignes.every(estNonFacturable);

  return (
    <div className="max-w-6xl mx-auto son-panel p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Smile className="h-6 w-6 mr-2 text-blue-600" />
          {consultation ? 'Modifier la consultation' : 'Nouvelle consultation'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Fermer la consultation"
          title="Fermer la consultation"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {isLocked && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Consultation verrouillée</h3>
              <p className="mt-1 text-sm text-yellow-700">
                {consultation?.sans_facturation
                  ? 'Cette consultation de suite de soins a été clôturée sans facturation. Elle ne peut plus être modifiée.'
                  : 'Cette consultation est verrouillée car une facture payée a été créée. Elle ne peut plus être modifiée.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Informations générales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient *
            </label>
            <PatientSearchSelect
              value={selectedPatientId}
              onChange={(patientId) => {
                setValue('patient', patientId || 0);
              }}
              error={errors.patient?.message}
              placeholder="Rechercher un patient par nom, prénom ou téléphone..."
              disabled={isLocked}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Médecin *
            </label>
            <select
              {...register('doctor', { required: 'Le médecin est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              disabled={isLocked}
            >
              <option value="">Sélectionner un médecin</option>
              {doctors.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.user_details ? (
                    `Dr. ${doctor.user_details.first_name} ${doctor.user_details.last_name}`
                  ) : (
                    doctor.user ? (
                      `Dr. ${doctor.user.first_name} ${doctor.user.last_name}`
                    ) : `Médecin #${doctor.id}`
                  )}
                </option>
              ))}
            </select>
            {errors.doctor && <p className="text-red-500 text-sm mt-1">{errors.doctor.message}</p>}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de consultation *
            </label>
            <input
              type="datetime-local"
              {...register('date_consultation', { required: 'La date est requise' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              disabled={isLocked}
            />
            {errors.date_consultation && <p className="text-red-500 text-sm mt-1">{errors.date_consultation.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Motif de consultation *
          </label>
          <textarea
            {...register('motif_consultation', { required: 'Le motif est requis' })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Raison de la visite..."
            disabled={isLocked}
          />
          {errors.motif_consultation && <p className="text-red-500 text-sm mt-1">{errors.motif_consultation.message}</p>}
        </div>

        {/* Diagramme dentaire */}
        {selectedPatientId && (
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Sélection des dents concernées</h3>
              <button
                type="button"
                onClick={() => setShowDentalDiagram(!showDentalDiagram)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                {showDentalDiagram ? 'Masquer' : 'Afficher'} le diagramme
              </button>
            </div>
            
            {showDentalDiagram && (
              <DiagrammeDentaire
                value={odontogramme}
                onChange={isLocked ? () => {} : setOdontogramme}
                patientAge={selectedPatient?.age}
                readOnly={isLocked}
              />
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Examen clinique
            </label>
            <textarea
              {...register('examen_clinique')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Observations cliniques..."
              disabled={isLocked}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Diagnostic
            </label>
            <textarea
              {...register('diagnostic')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Diagnostic établi..."
              disabled={isLocked}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plan de traitement
          </label>
          <textarea
            {...register('plan_traitement')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            placeholder="Plan de traitement proposé..."
            disabled={isLocked}
          />
        </div>

        {/* Actes réalisés */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Traitements réalisés</h3>
            {!isLocked && (
              <button
                type="button"
                onClick={ajouterTraitement}
                className="flex items-center px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter un traitement
              </button>
            )}
          </div>

          {actes.map((acte, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 p-4 border rounded-lg bg-gray-50">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de traitement</label>
                <select
                  value={acte.treatment_type}
                  onChange={(e) => modifierTraitement(index, 'treatment_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900"
                  aria-label="Sélectionner un type de traitement"
                  disabled={isLocked}
                >
                  <option value="">Sélectionner</option>
                  {treatmentTypes.map(treatmentType => (
                    <option key={treatmentType.id} value={treatmentType.id}>
                      {treatmentType.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={acte.quantite}
                  onChange={(e) => modifierTraitement(index, 'quantite', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900"
                  placeholder="Quantité"
                  title="Quantité"
                  disabled={isLocked}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prix unitaire</label>
                {estNonFacturable(acte) ? (
                  <div className="flex items-center h-[38px]">
                    <span className="son-chip son-chip-gray" title="Séance d'un traitement déjà payé : jamais facturée">
                      Suite de soins · non facturé
                    </span>
                  </div>
                ) : (
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={acte.prix_unitaire}
                    onChange={(e) => modifierTraitement(index, 'prix_unitaire', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-900"
                    placeholder="Prix unitaire"
                    title="Prix unitaire"
                    disabled={isLocked}
                  />
                )}
              </div>

              {!isLocked && (
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => supprimerActe(index)}
                    className="flex items-center px-3 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                    title="Supprimer le traitement"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Ordonnances */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Pill className="h-5 w-5 mr-2 text-green-600" />
              Ordonnances
            </h3>
            <div className="flex items-center space-x-3">
              {!isLocked && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Mode:</label>
                  <button
                    type="button"
                    onClick={() => setUseStructuredOrdonnance(true)}
                    className={`px-3 py-1 text-xs rounded ${
                      useStructuredOrdonnance
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Médicaments
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseStructuredOrdonnance(false)}
                    className={`px-3 py-1 text-xs rounded ${
                      !useStructuredOrdonnance
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Texte libre
                  </button>
                </div>
              )}
              {!useStructuredOrdonnance && !isLocked && (
                <button
                  type="button"
                  onClick={ajouterOrdonnance}
                  className="flex items-center px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter une ordonnance
                </button>
              )}
            </div>
          </div>

          {useStructuredOrdonnance ? (
            // Mode structuré avec sélection de médicaments
            <div className="space-y-6">
              <MedicamentSelector
                medicamentsOrdonnance={medicamentsOrdonnance}
                onMedicamentsChange={setMedicamentsOrdonnance}
              />
              
              {/* Contenu libre complémentaire */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-3">Prescription libre complémentaire</h5>
                <textarea
                  value={ordonnanceContenuLibre}
                  onChange={(e) => setOrdonnanceContenuLibre(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                  placeholder="Ajoutez des instructions générales ou des prescriptions non-médicamenteuses..."
                />
              </div>

              {/* Notes du médecin */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-3">Notes du médecin</h5>
                <textarea
                  value={ordonnanceNotes}
                  onChange={(e) => setOrdonnanceNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                  placeholder="Notes privées ou recommandations spéciales..."
                />
              </div>
            </div>
          ) : (
            // Mode texte libre classique
            <div>
              {ordonnances.map((ordonnance, index) => (
                <div key={index} className="flex gap-4 mb-4">
                  <textarea
                    value={ordonnance}
                    onChange={(e) => modifierOrdonnance(index, e.target.value)}
                    rows={3}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
                    placeholder="Contenu de l'ordonnance..."
                  />
                  <button
                    type="button"
                    onClick={() => supprimerOrdonnance(index)}
                    className="flex items-center px-3 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600 h-fit"
                    title="Supprimer l'ordonnance"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes complémentaires
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Notes diverses..."
            disabled={isLocked}
          />
        </div>

        {/* Boutons d'action */}
        {caisseFeedback && (
          <div className="pt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-2">
            {caisseFeedback}
          </div>
        )}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            {isLocked ? 'Fermer' : 'Annuler'}
          </button>
          {!isLocked && (
            <>
              <button
                type="submit"
                onClick={() => { envoyerCaisseRef.current = false; }}
                disabled={consultationMutation.isLoading}
                className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                {consultationMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                type="submit"
                onClick={() => { envoyerCaisseRef.current = true; }}
                disabled={consultationMutation.isLoading}
                title={suiteSeule
                  ? 'Suite de soins : enregistre et clôture la consultation, sans bon ni facture'
                  : 'Enregistre la consultation et envoie un bon à la caisse'}
                className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {suiteSeule ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Valider &amp; clôturer sans facturation
                  </>
                ) : (
                  <>
                    <Banknote className="h-4 w-4 mr-2" />
                    Valider &amp; envoyer à la caisse
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default ConsultationForm;